import { useTranslations } from '@vocab/react';
import { Drawer, Text, Actions, Button, Dialog } from 'braid-design-system';
import { useState, useCallback, useEffect, type ReactNode } from 'react';

import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useMode } from 'src/components/Router/useMode';
import { EDIT } from 'src/consts';
import { type Form, useFormContext } from 'src/context/FormContext';
import type { DrawerCard } from 'src/types';

import translations from './.vocab';

export interface DirtyFormProps {
  onFormDirtyChange: (isFormDirty: boolean) => void;
}

interface Props {
  title: string;
  description?: string | JSX.Element;
  component: (prop: DirtyFormProps) => JSX.Element | null | ReactNode;
  card: DrawerCard;
  formName: Form;
}

export const FormDrawer = ({
  title,
  description,
  component: Component,
  card,
  formName,
}: Props) => {
  const { t } = useTranslations(translations);
  const enterReadMode = useEnterReadMode();
  const formContext = useFormContext();

  const dirty = formContext[formName].dirty;
  const mode = useMode(card);

  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);

  useEffect(() => {
    setShowDrawer(mode === EDIT);
  }, [mode]);

  // Whenever the form is mark as dirty = true, if user try to close the drawer
  // an dialog box will prompt to user whether to discard the unsafe changes.
  // In order to ensure this works without error, please connect
  // the onFormDirtyChange function accordingly in the Edit component
  // you may want to reuse the `DirtyFormProps` type exported above too.
  const onFormDirtyChangeFn = useCallback(
    (isFormDirty: boolean) => {
      formContext.setDirtyState(formName, isFormDirty);
    },
    // NOTE: adding formContext to the dependencies array will cause infinite render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formName],
  );

  const closeDrawerFn = useCallback(() => {
    if (!dirty) {
      enterReadMode();
    } else {
      setShowDiscardDialog(true);

      return false;
    }
  }, [dirty, enterReadMode]);

  const discardChangesFn = useCallback(() => {
    setShowDiscardDialog(false);
    enterReadMode();
  }, [enterReadMode]);

  return (
    <>
      <Drawer
        id={`${card}-form-drawer`}
        title={title}
        open={showDrawer}
        onClose={closeDrawerFn}
        closeLabel={t('Close')}
        width="small"
        data={{ automation: `${card}-form-drawer` }}
        {...(description && {
          description: <Text tone="secondary">{description}</Text>,
        })}
      >
        {Component ? (
          <Component onFormDirtyChange={onFormDirtyChangeFn} />
        ) : null}
      </Drawer>
      <Dialog
        id={`${card}-discard-dialog`}
        title={t('Discard changes')}
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        closeLabel={t('Close')}
        width="xsmall"
      >
        <Text>{t('Are you sure you want to leave without saving?')}</Text>
        <Actions>
          <Button tone="critical" variant="ghost" onClick={discardChangesFn}>
            {t('Discard')}
          </Button>
          <Button
            tone="formAccent"
            variant="transparent"
            onClick={() => setShowDiscardDialog(false)}
          >
            {t('Cancel')}
          </Button>
        </Actions>
      </Dialog>
    </>
  );
};
